"use client";

import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@workspace/ui/components/select";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { queueManagementApi } from "../../queues/api";
import { deviceManagementApi } from "../api";
import {
	DEVICE_STATUS_LABELS,
	type Device,
	type DeviceStatus,
	type UpdateDeviceInput,
} from "../types";

interface EditDeviceDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
	device: Device;
}

const isTerminalDevice = (type: string) => type === "Till" || type === "Tablet";

// Radix Select disallows an empty-string item value, so "no printer" uses a sentinel mapped to null.
const NO_RECEIPT_PRINTER = "__none__";

const editDeviceSchema = z.object({
	name: z
		.string()
		.min(1, "Device name is required")
		.max(100, "Device name must not exceed 100 characters"),
	status: z.enum(["Active", "Inactive"], {
		message: "Please select a status",
	}),
	suppressedQueueIds: z.array(z.string()),
	receiptPrinterId: z.string().nullable(),
});

type EditDeviceFormValues = z.infer<typeof editDeviceSchema>;

export function EditDeviceDialog({
	open,
	onOpenChange,
	tenant,
	device,
}: EditDeviceDialogProps) {
	const queryClient = useQueryClient();
	const showSuppression = isTerminalDevice(device.type);

	const queuesQuery = useQuery({
		...queueManagementApi.getQueuesOptions(tenant, device.venueId),
		enabled: open && showSuppression,
	});

	const availableQueues = queuesQuery.data?.queues ?? [];

	// HA-1027 — the printers on this venue's print proxies, for the terminal's receipt-printer assignment.
	const receiptPrintersQuery = useQuery({
		...deviceManagementApi.getReceiptPrintersOptions(tenant, device.id),
		enabled: open && showSuppression,
	});

	const availableReceiptPrinters = receiptPrintersQuery.data?.printers ?? [];

	const mutation = useMutation({
		...deviceManagementApi.updateDeviceOptions(tenant, device.id),
		onSuccess: (result) => {
			if (result.success) {
				toast.success("Device updated successfully.");
				queryClient.invalidateQueries({
					queryKey: [tenant, "devices"],
				});
				handleClose(false);
			} else {
				const errorDetails = result.details as { message?: string } | undefined;
				toast.error(
					errorDetails?.message || "Failed to update device. Please try again.",
				);
			}
		},
		onError: () => {
			toast.error("Unable to update device. Please try again.");
		},
	});

	const form = useAppForm({
		defaultValues: {
			name: device.name,
			status: device.status,
			suppressedQueueIds: device.suppressedQueueIds ?? [],
			receiptPrinterId: device.receiptPrinterId ?? null,
		} as EditDeviceFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: editDeviceSchema,
		},
		onSubmit: async ({ value }) => {
			const input: UpdateDeviceInput = {};

			if (value.name !== device.name) {
				input.name = value.name.trim();
			}

			if (value.status !== device.status) {
				input.status = value.status;
			}

			if (showSuppression) {
				const sortedNew = [...value.suppressedQueueIds].sort();
				const sortedOld = [...(device.suppressedQueueIds ?? [])].sort();
				if (JSON.stringify(sortedNew) !== JSON.stringify(sortedOld)) {
					input.suppressedQueueIds = value.suppressedQueueIds;
				}

				const newPrinterId = value.receiptPrinterId ?? null;
				const oldPrinterId = device.receiptPrinterId ?? null;
				if (newPrinterId !== oldPrinterId) {
					input.properties = {
						...(device.properties ?? {}),
						receiptPrinterId: newPrinterId,
					};
				}
			}

			// Only submit if there are changes
			if (Object.keys(input).length === 0) {
				handleClose(false);
				return;
			}

			await mutation.mutateAsync(input);
		},
	});

	// Reset form when device changes
	React.useEffect(() => {
		if (open) {
			form.reset({
				name: device.name,
				status: device.status,
				suppressedQueueIds: device.suppressedQueueIds ?? [],
				receiptPrinterId: device.receiptPrinterId ?? null,
			});
		}
	}, [device, open, form]);

	const handleClose = (o: boolean) => {
		if (!o) {
			form.reset();
			mutation.reset();
		}
		onOpenChange(o);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit Device</DialogTitle>
					<DialogDescription>
						Update the device name or status. Device identifier and type cannot
						be changed.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					{mutation.isError && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription>
								Unable to update device. Please try again.
							</AlertDescription>
						</Alert>
					)}

					<div className="rounded-md bg-muted p-3 text-sm">
						<div className="grid grid-cols-2 gap-2">
							<div>
								<span className="text-muted-foreground">Identifier:</span>
								<span className="ml-2 font-mono">{device.identifier}</span>
							</div>
							<div>
								<span className="text-muted-foreground">Venue:</span>
								<span className="ml-2">{device.venueName}</span>
							</div>
						</div>
					</div>

					<form.AppField name="name">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Device Name</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="e.g., Bar Till 1"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormDescription>
									A friendly name to identify this device.
								</field.FormDescription>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="status">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Status</field.FormLabel>
								<Select
									value={field.state.value}
									onValueChange={(value) =>
										field.handleChange(value as DeviceStatus)
									}
								>
									<field.FormControl>
										<SelectTrigger>
											<SelectValue placeholder="Select status" />
										</SelectTrigger>
									</field.FormControl>
									<SelectContent>
										{(
											Object.entries(DEVICE_STATUS_LABELS) as [
												DeviceStatus,
												string,
											][]
										).map(([value, label]) => (
											<SelectItem key={value} value={value}>
												{label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<field.FormDescription>
									Active devices are available for use.
								</field.FormDescription>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					{showSuppression && (
						<form.AppField name="suppressedQueueIds">
							{(field) => (
								<field.FormItem>
									<field.FormLabel>Suppressed Queues</field.FormLabel>
									<div className="space-y-2">
										{queuesQuery.isLoading && (
											<div className="flex items-center gap-2 text-sm text-muted-foreground">
												<Loader2 className="h-3 w-3 animate-spin" />
												Loading queues...
											</div>
										)}
										{availableQueues.length === 0 && !queuesQuery.isLoading && (
											<p className="text-sm text-muted-foreground">
												No queues configured for this venue.
											</p>
										)}
										{availableQueues.map((queue) => (
											<div
												key={queue.id}
												className="flex items-center space-x-2"
											>
												<Checkbox
													id={`suppress-queue-${queue.id}`}
													checked={field.state.value.includes(queue.id)}
													onCheckedChange={(checked) => {
														const current = field.state.value;
														if (checked) {
															field.handleChange([...current, queue.id]);
														} else {
															field.handleChange(
																current.filter((id: string) => id !== queue.id),
															);
														}
													}}
												/>
												<Label
													htmlFor={`suppress-queue-${queue.id}`}
													className="text-sm"
												>
													{queue.name}
												</Label>
											</div>
										))}
									</div>
									<field.FormDescription>
										Items routed to suppressed queues will not print from this
										terminal but will still be marked as sent.
									</field.FormDescription>
									<field.FormMessage />
								</field.FormItem>
							)}
						</form.AppField>
					)}

					{showSuppression && (
						<form.AppField name="receiptPrinterId">
							{(field) => (
								<field.FormItem>
									<field.FormLabel>Receipt Printer</field.FormLabel>
									<Select
										value={field.state.value ?? NO_RECEIPT_PRINTER}
										onValueChange={(value) =>
											field.handleChange(
												value === NO_RECEIPT_PRINTER ? null : value,
											)
										}
									>
										<field.FormControl>
											<SelectTrigger>
												<SelectValue placeholder="Select receipt printer" />
											</SelectTrigger>
										</field.FormControl>
										<SelectContent>
											<SelectItem value={NO_RECEIPT_PRINTER}>None</SelectItem>
											{availableReceiptPrinters.map((printer) => (
												<SelectItem
													key={printer.recordId}
													value={printer.recordId}
												>
													{printer.testPrintConfirmed
														? printer.label
														: `${printer.label} (test print not confirmed)`}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<field.FormDescription>
										The receipt printer this terminal sends customer bills and
										receipts to.
									</field.FormDescription>
									<field.FormMessage />
								</field.FormItem>
							)}
						</form.AppField>
					)}

					<DialogFooter>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
						>
							{([canSubmit]) => (
								<>
									<Button
										type="button"
										variant="outline"
										onClick={() => handleClose(false)}
										disabled={mutation.isPending}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!canSubmit || mutation.isPending}
									>
										{mutation.isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										Save Changes
									</Button>
								</>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
